import { realtimeService } from '@peritasai/data-access';
import {
  AssetEntity,
  getChildLocations,
  LocationEntity,
  useAllLocations,
} from '@peritasai/repository';
import {
  AssetLocationUpdatedEvent,
  AssetType,
  AssetUpdateEventFilters,
  RealtimeEventType,
  type CaseUpdatedEvent,
  type EventSubscription,
} from '@peritasai/types';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReleaseToDeconService } from '../services/release-to-decon.service';
import {
  DetectedTray,
  ReleaseToDeconStep,
  ReleaseTrayStatus,
} from '../types/release-to-decon';

type UseReleaseToDeconDiscoveryOptions = {
  step: ReleaseToDeconStep;
  setStep: Dispatch<SetStateAction<ReleaseToDeconStep>>;
  selectedLocation?: LocationEntity | null;
  onReset?: () => void;
};

type UseReleaseToDeconDiscoveryResult = {
  detectedTrays: DetectedTray[];
  alreadyDisplayedAssetIds: Set<string>;
  clearDetectedTrays: () => void;
  handleCancelDiscovery: () => void;
};
const INACTIVITY_INTERVAL_MS = 100;
const INACTIVITY_TIMEOUT_MS = 10_000;
const ENABLE_INACTIVITY_RESET = true;
const logDebug = (...args: unknown[]) => {
  console.log('[TEMP-SSE-DEBUG][ReleaseDiscovery]', ...args);
};

const formatDetectedAtLabel = (timestamp?: Date | string | null) => {
  const detectedAt = timestamp ? new Date(timestamp) : new Date();
  const timeLabel = detectedAt
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .replace(/\s/g, '')
    .toLowerCase();
  return `${timeLabel} • Detected`;
};

export const useReleaseToDeconDiscovery = ({
  step,
  setStep,
  selectedLocation,
}: UseReleaseToDeconDiscoveryOptions): UseReleaseToDeconDiscoveryResult => {
  const [detectedTrays, setDetectedTrays] = useState<DetectedTray[]>([]);
  //Holds a list of asset ids that have previously displayed
  const [alreadyDisplayedAssetIds, setAlreadyDisplayedAssetIds] = useState<
    Set<string>
  >(() => new Set());
  const alreadyDisplayedAssetIdsRef = useRef(alreadyDisplayedAssetIds);
  alreadyDisplayedAssetIdsRef.current = alreadyDisplayedAssetIds;

  const discoverableAssetTypes = [AssetType.TRAY];
  const locationQuery = useAllLocations();

  const releaseToDeconService = useReleaseToDeconService();
  //breaks useEffect dependcy loop
  const releaseToDeconServiceRef = useRef(releaseToDeconService);
  releaseToDeconServiceRef.current = releaseToDeconService;

  const streamConnectedRef = useRef(false);
  const startScanRequestInFlightRef = useRef(false);
  const streamAbortRef = useRef<AbortController | null>(null);
  const streamErrorCountRef = useRef(0);
  const inactivityTimerRef = useRef<number | null>(null);

  const clearActiveStream = useCallback(() => {
    if (streamAbortRef.current) {
      streamAbortRef.current.abort();
      streamAbortRef.current = null;
    }
  }, []);

  const clearDetectedTrays = useCallback(() => {
    setAlreadyDisplayedAssetIds(new Set());
    setDetectedTrays([]);
  }, []);

  const clearSessionState = useCallback(() => {
    clearActiveStream();
    clearDetectedTrays();

    startScanRequestInFlightRef.current = false;
    streamErrorCountRef.current = 0;
    streamConnectedRef.current = false;
  }, [clearActiveStream, clearDetectedTrays]);

  const handleCancelDiscovery = useCallback(() => {
    clearDetectedTrays();
    setStep(ReleaseToDeconStep.SELECT_LOCATION);
  }, [clearDetectedTrays, setStep]);

  const resetInactivityTimer = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (inactivityTimerRef.current) {
      window.clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (!ENABLE_INACTIVITY_RESET) {
      return;
    }
    inactivityTimerRef.current = window.setTimeout(() => {
      setDetectedTrays((prev) => {
        //Counts up timer value until max then remove.
        const newDetectedTrays = prev
          .map((old) => ({
            ...old,
            timer:
              old.status !== ReleaseTrayStatus.PENDING
                ? old.timer + INACTIVITY_INTERVAL_MS
                : 0,
          }))
          .filter((old) => old.timer <= INACTIVITY_TIMEOUT_MS);

        if (newDetectedTrays.some((item) => item.status !== ReleaseTrayStatus.PENDING)) {
          resetInactivityTimer();
        }
        return newDetectedTrays;
      });
    }, INACTIVITY_INTERVAL_MS);
  }, []);

  const resetInactivityTimerRef = useRef(resetInactivityTimer);

  useEffect(() => {
    resetInactivityTimerRef.current = resetInactivityTimer;
  }, [resetInactivityTimer]);

  const locationId = selectedLocation?.id ?? null;
  const locationChildIds = useMemo(
    () =>
      selectedLocation
        ? [
            selectedLocation,
            ...getChildLocations(locationQuery.data, selectedLocation?.id),
          ].map((loc) => loc?.id)
        : [],
    [locationQuery.data, selectedLocation]
  );
  const assetTypeFilter = discoverableAssetTypes.join(',');

  useEffect(() => {
    let isActive = true;
    const shouldScan = step === ReleaseToDeconStep.LOADING_HUB;

    logDebug('effect:start', {
      step,
      shouldScan,
      hasSelectedLocation: !!selectedLocation,
      hasActiveStream: !!streamAbortRef.current,
    });

    if (!locationId) {
      clearSessionState();
      logDebug('effect:skip-no-location');
      return;
    }

    if (!shouldScan) {
      logDebug('scan:disabled-cleanup', {
        step,
        hasActiveStream: !!streamAbortRef.current,
      });

      clearSessionState();
      return;
    }

    const loadTrays = () => {
      if (startScanRequestInFlightRef.current) {
        return;
      }

      if (!locationId) {
        return;
      }

      startScanRequestInFlightRef.current = true;
      logDebug('scan:start-requested', {
        locationId: locationId,
      });

      const releaseToDeconService = releaseToDeconServiceRef.current;
      void releaseToDeconService
        .getTraysForLocationAndChild(locationId)
        .then(async (result) => {
          if (!isActive) return;

          const NOW = Date.now();
          const alreadyDisplayedAssetIds = alreadyDisplayedAssetIdsRef.current;
          const filteredResult = result.filter((item: AssetEntity) => {
            if (item.caseId != null) return false;

            if (alreadyDisplayedAssetIds.has(item.id)) return false;

            if (item.locationUpdatedAt) {
              const diff = Math.abs(NOW - item.locationUpdatedAt.getTime());
              return diff <= 5 * 60 * 1000;
            }
            return false;
          });

          logDebug('scan:tray-response', {
            assets: filteredResult,
          });

          setAlreadyDisplayedAssetIds(
            (prev) =>
              new Set([...prev, ...filteredResult.map((tray) => tray.id)])
          );
          setDetectedTrays((prev: DetectedTray[]) => {
            return filteredResult.map(
              (tray: AssetEntity): DetectedTray => ({
                id: tray.id,
                name: tray.name,
                subtitle: tray.referenceNumber,
                detectedAtLabel: formatDetectedAtLabel(tray.locationUpdatedAt),
                status: tray.caseId
                  ? ReleaseTrayStatus.PENDING
                  : ReleaseTrayStatus.RELEASED,
                caseId: tray.caseId,
                timer: 0,
              })
            );
          });
          resetInactivityTimerRef.current();
          startScanRequestInFlightRef.current = false;
        })
        .catch(() => {
          if (!isActive) return;
          startScanRequestInFlightRef.current = false;
          setStep(ReleaseToDeconStep.HUB_OFFLINE);
        });
    };

    if (streamAbortRef.current) {
      logDebug('stream:skip-existing-or-pending', {
        hasActiveStream: !!streamAbortRef.current,
      });
      return;
    }

    const filter: AssetUpdateEventFilters = {
      type: assetTypeFilter,
    };
    const subscriptions: EventSubscription[] = [
      {
        eventType: RealtimeEventType.ASSET_LOCATION_UPDATED,
        filters: filter,
      },
      {
        eventType: RealtimeEventType.CASE_UPDATED,
      },
    ];

    const controller = new AbortController();
    streamAbortRef.current = controller;
    streamErrorCountRef.current = 0;
    logDebug('stream:connect-start', {
      subscriptions,
    });

    void realtimeService.connect({
      subscriptions,
      signal: controller.signal,
      onEvent(event) {
        console.log(event);
        if (event?.type === RealtimeEventType.ASSET_LOCATION_UPDATED) {
          streamErrorCountRef.current = 0;
          const eventData = (event as AssetLocationUpdatedEvent).data;
          const assetData = eventData.asset;
          if (
            !assetData.location?.id ||
            !locationChildIds.includes(assetData.location?.id) ||
            assetData.type !== AssetType.TRAY
          ) {
            logDebug('stream:event-skip-filter-mismatch', {
              locationId: locationId,
              assetId: assetData.id,
            });
            return;
          }

          const alreadyDisplayedAssetIds = alreadyDisplayedAssetIdsRef.current;
          if (alreadyDisplayedAssetIds.has(assetData.id)) {
            logDebug('stream:event-skip-duplicate', {
              locationId: locationId,
              assetId: assetData.id,
            });
            return;
          }

          setAlreadyDisplayedAssetIds(
            (prev) => new Set([...prev, assetData.id])
          );
          setDetectedTrays((prev: DetectedTray[]) => {
            const newTray: DetectedTray = {
              id: assetData.id,
              name: assetData.name ?? 'Detected tray',
              subtitle: assetData.referenceNumber,
              assetType: AssetType.TRAY,
              detectedAtLabel: formatDetectedAtLabel(
                assetData.locationUpdatedAt
              ),
              status: assetData.caseId
                ? ReleaseTrayStatus.PENDING
                : ReleaseTrayStatus.RELEASED,
              caseId: assetData.caseId ?? null,
              timer: 0,
            };
            return [...prev.filter((item) => item.id !== newTray.id), newTray];
          });
          resetInactivityTimerRef.current();
          logDebug('stream:event-tray-detected', {
            assetId: assetData.id,
            assetName: assetData.name,
          });
        } else if (event.type === RealtimeEventType.CASE_UPDATED) {
          const caseEvent = event as CaseUpdatedEvent;
          const trackedTrayItems = [
            ...(caseEvent.data?.case?.buildChecklist?.trackedItems?.trays ??
              []),
            ...(caseEvent.data?.case?.buildChecklist?.trackedItems
              ?.unexpectedItems ?? []),
          ];

          const alreadyDisplayedAssetIds = alreadyDisplayedAssetIdsRef.current;
          const assignedIds = new Set<string>();
          let update = false;
          for (const trayGroup of trackedTrayItems) {
            for (const item of trayGroup.items ?? []) {
              if (!item.released && item.assetId) {
                assignedIds.add(item.assetId);
                update = update || alreadyDisplayedAssetIds.has(item.assetId);
              }
            }
          }

          if (!update) return;

          setAlreadyDisplayedAssetIds((prev) => {
            const result = new Set<string>();
            for (const item of prev) {
              if (!assignedIds.has(item)) {
                result.add(item);
              }
            }
            return result;
          });

          resetInactivityTimerRef.current();
          return;
        }
      },
      onStateChange(state) {
        if (controller.signal.aborted) return;
        logDebug('stream:state-change', {
          connected: state.connected,
          errorName: state.error?.name ?? null,
          errorMessage: state.error?.message ?? null,
          errorCount: streamErrorCountRef.current,
        });
        if (state.connected) {
          streamConnectedRef.current = true;
          streamErrorCountRef.current = 0;
          loadTrays();
          return;
        }
        streamConnectedRef.current = false;
        streamAbortRef.current = null;

        if (!state.error) {
          logDebug('stream:closed-without-error');
          setStep(ReleaseToDeconStep.HUB_OFFLINE);
          return;
        }

        const errorMessage = state.error.message ?? '';
        const isAbortError =
          state.error.name === 'AbortError' || /abort/i.test(errorMessage);
        const is401 = /401|Unauthorized/i.test(errorMessage);
        if (isAbortError || is401) return;

        const isUnexpectedClose = /stream closed unexpectedly/i.test(
          errorMessage
        );
        if (isUnexpectedClose) {
          logDebug('stream:unexpected-close-transition', {
            errorName: state.error.name,
            errorMessage,
          });
          setStep(ReleaseToDeconStep.HUB_OFFLINE);
          return;
        }

        streamErrorCountRef.current += 1;
        if (streamErrorCountRef.current < 3) return;

        logDebug('stream:offline-transition', {
          errorName: state.error.name,
          errorMessage,
          errorCount: streamErrorCountRef.current,
        });
        console.error('Discovery SSE error', state.error);
        setStep(ReleaseToDeconStep.HUB_OFFLINE);
      },
    });

    return () => {
      logDebug('effect:cleanup', {
        hasActiveStream: !!streamAbortRef.current,
      });

      if (streamAbortRef.current) {
        streamAbortRef.current.abort();
        streamAbortRef.current = null;
      }
      isActive = false;
    };
  }, [clearSessionState, setStep, step]);

  return {
    detectedTrays,
    alreadyDisplayedAssetIds,
    clearDetectedTrays,
    handleCancelDiscovery,
  };
};
